/**
 * JWT Exchange API
 *
 * Handles exchanging JWT tokens for authenticated session cookies.
 *
 * NOTE: This calls the auth service which runs on the tenant subdomain ({slug}.{domain}),
 * NOT on the user portal origin (app.{domain}). This is a cross-origin request.
 * CORS is configured with wildcard subdomain patterns (e.g., https://*.newcore.io)
 * and cookies on .{domain} are shared across subdomains.
 */

import { authServiceOrigin } from '@/common/tenant-utils';
import type { JwtExchangeResponse } from '@/features/onboarding/types/onboarding.types';

/**
 * Exchange JWT token for authenticated session cookie
 *
 * IMPORTANT: This calls auth on the tenant subdomain ({slug}.{domain}),
 * NOT on the user portal origin (app.{domain}).
 */
export async function exchangeJwtForSession(
  exchangeToken: string
): Promise<JwtExchangeResponse> {
  // auth is the IdP service that runs on the tenant subdomain
  const origin = authServiceOrigin();
  const jwtExchangeUrl = origin
    ? `${origin}/auth/jwt-exchange`
    : '/auth/jwt-exchange';

  const response = await fetch(jwtExchangeUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ exchangeToken }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail ?? error.message ?? 'Authentication failed');
  }

  return response.json();
}
